import { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import { cleanupNewAccounts } from 'redux_store/slices/userAccountManagementSlice';
import { getVisiblePermissionCount } from 'utils/permissionUtils';

const useStyles = makeStyles((theme) => ({
  accountsSidebar: {
    background: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0, // Important for flex children
  },
  searchContainer: {
    padding: theme.spacing(2.5, 2.5, 0, 2.5),
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    background: '#f8f9fa',
    position: 'relative',
    zIndex: 2,
  },
  searchWrapper: {
    display: 'flex',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  searchInputWrapper: {
    position: 'relative',
    flex: 1,
  },
  searchInput: {
    width: '100%',
    padding: theme.spacing(1.5, 1.5, 1.5, 5),
    background: 'white',
    border: '1px solid #e1e4e8',
    borderRadius: 8,
    fontSize: 14,
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
    '&::placeholder': {
      color: '#999',
    },
  },
  searchIcon: {
    position: 'absolute',
    left: theme.spacing(1.5),
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
    fontSize: 20,
  },
  accountsList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 2.5, 0, 2.5),
    minHeight: 0, // Allow shrinking
    maxHeight: '100%', // Ensure it doesn't exceed container
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
  addAccountBtn: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    fontSize: 24,
    fontWeight: 300,
    boxShadow: '0 2px 8px rgba(72, 187, 120, 0.2)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(72, 187, 120, 0.3)',
    },
  },
  highlightText: {
    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
    color: '#856404',
    padding: '1px 3px',
    borderRadius: 3,
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(133, 100, 4, 0.2)',
  },
  accountListItem: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    background: 'white',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.3s',
    border: '2px solid transparent',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    '&:hover': {
      borderColor: '#667eea',
      transform: 'translateX(5px)',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.15)',
    },
  },
  accountListItemActive: {
    borderColor: '#667eea',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
    transform: 'translateX(5px)',
  },
  accountItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  accountName: {
    fontWeight: 600,
    fontSize: 16,
    color: '#333',
  },
  accountRole: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  badgePrimary: {
    background: '#e6f0ff',
    color: '#0066cc',
  },
  badgeSuccess: {
    background: '#e6f9e6',
    color: '#008800',
  },
  badgeWarning: {
    background: '#fff3cd',
    color: '#cc7700',
  },
  badgeNew: {
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: 'white',
    animation: '$pulse 2s infinite',
    boxShadow: '0 2px 8px rgba(72, 187, 120, 0.3)',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 2px 8px rgba(72, 187, 120, 0.3)',
    },
    '50%': {
      boxShadow: '0 4px 12px rgba(72, 187, 120, 0.5)',
      transform: 'scale(1.05)',
    },
    '100%': {
      boxShadow: '0 2px 8px rgba(72, 187, 120, 0.3)',
    },
  },
}));

const AccountsSidebar = ({ setIsModalOpen, accounts, selectedAccount, onAccountSelect, userUuid, hasUnsavedChanges, onUnsavedChangesWarning, shouldOpenAddAccount, setShouldOpenAddAccount, accountUuid }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const [searchQuery, setSearchQuery] = useState('');
  const addButtonRef = useRef(null);

  // Effect to handle deferred opening of add account modal
  useEffect(() => {
    if (shouldOpenAddAccount) {
      setIsModalOpen(true);
      setShouldOpenAddAccount(false);
    }
  }, [shouldOpenAddAccount, setShouldOpenAddAccount]);

  // Auto-cleanup new accounts after 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(cleanupNewAccounts());
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  // Filter accounts based on search query and sort by new, current, then others
  const filteredAccounts = accounts
    .filter(account =>
      account.accountBusinessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.role.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // First priority: new accounts
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      // Second priority: current account
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      return 0;
    });

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleAddAccountClick = (event) => {
    // Check for unsaved changes before opening modal
    if (hasUnsavedChanges) {
      onUnsavedChangesWarning('addAccount');
      return;
    }
    setIsModalOpen(true);
  };

  // Function to highlight matching text
  const highlightText = (text, query) => {
    if (!query.trim()) {
      return text;
    }

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = regex.test(part);
          return isMatch ? (
            <span key={index} className={classes.highlightText}>
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  };

  return (
    <div className={classes.accountsSidebar}>
      {/* Fixed Top Search with Add Button */}
      <div className={classes.searchContainer}>
        <div className={classes.searchWrapper}>
          <div className={classes.searchInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <input
              type="text"
              placeholder={titles.searchAccounts}
              value={searchQuery}
              onChange={handleSearchChange}
              className={classes.searchInput}
            />
          </div>
          <button
            ref={addButtonRef}
            className={classes.addAccountBtn}
            onClick={handleAddAccountClick}
            title={titles.addAccountAccess}
          >
            +
          </button>
        </div>
      </div>

      {/* Scrollable Accounts List */}
      <div className={classes.accountsList}>
        {filteredAccounts.length === 0 && searchQuery ? (
          <div style={{
            textAlign: 'center',
            color: '#666',
            marginTop: 40,
            fontStyle: 'italic'
          }}>
            {titles.noAccountsFound} "{searchQuery}"
          </div>
        ) : (
          filteredAccounts.map((account) => (
            <div
              key={account.accountUUID}
              className={`${classes.accountListItem} ${
                selectedAccount === account.accountUUID ? classes.accountListItemActive : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (onAccountSelect) {
                  onAccountSelect(account.accountUUID);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className={classes.accountItemHeader}>
                <div className={classes.accountName}>
                  {highlightText(account.accountBusinessName, searchQuery)}
                </div>
                {account.isNew ? (
                  <span className={`${classes.badge} ${classes.badgeNew}`}>
                    NEW
                  </span>
                ) : account.badge && (
                  <span className={`${classes.badge} ${classes[`badge${account.badge.type.charAt(0).toUpperCase() + account.badge.type.slice(1)}`]}`}>
                    {account.badge.text}
                  </span>
                )}
              </div>
              <div className={classes.accountRole}>
                {highlightText(account.role, searchQuery)} • {getVisiblePermissionCount(account.userPermissions)} {titles.permissionsCount}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AccountsSidebar;
